/**
 * Adaptive low-pass filter for GPS coordinates.
 *
 * Applies the recurrence:
 *
 *     θ_smooth = θ_prev + β · (θ_new − θ_prev)
 *
 * to each coordinate axis independently, where the smoothing factor β
 * scales dynamically based on the reported accuracy margin AND how far
 * the new reading has moved compared to that margin. This prevents the
 * pin from jittering when the user is standing still while still tracking
 * real movement quickly.
 *
 * Behaviour:
 *   • Very accurate fix (< 10 m) and small delta → β ≈ 0.15 (heavy damping)
 *   • Very accurate fix but movement > 2× accuracy → β ≈ 0.6 (fast follow)
 *   • Poor fix (> 100 m) → β ≈ 0.05 (essentially ignore drift)
 *   • First-ever fix → β = 1 (adopt directly, no smoothing history to blend)
 *
 * Usage:
 *   const filter = createCoordFilter();
 *   const { lat, lng, accuracy } = filter.smooth(browserPosition);
 */

// Approximate meters between two lat/lng points (equirectangular, fine at short distances).
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const midLat = toRad((lat1 + lat2) / 2);
  const x = dLng * Math.cos(midLat);
  const y = dLat;
  return Math.sqrt(x * x + y * y) * R;
}

/**
 * Compute the adaptive β for a given fix.
 *   accuracyM  — reported accuracy of the NEW reading, in meters.
 *   deltaM     — distance from previous smoothed position to new reading, in meters.
 *
 * Design:
 *   1. Base β decays as accuracy worsens: β_base = clamp(15 / accuracy, 0.05, 0.7)
 *   2. Movement boost: if delta > 2×accuracy, this is likely REAL movement, so
 *      trust the new reading more: β *= (1 + min((delta - 2×acc) / (5×acc), 2)).
 *   3. Final β is clamped to [0.05, 0.9] so we always let SOME signal through
 *      but never fully snap to raw noise.
 */
export function computeBeta(accuracyM, deltaM) {
  const acc = Math.max(1, Number(accuracyM) || 50);
  const delta = Math.max(0, Number(deltaM) || 0);

  // Base β decays as accuracy worsens.
  //   acc =  5 m → β_base = 0.40 (typical outdoor GPS)
  //   acc = 15 m → β_base = 0.40  (still capped)
  //   acc = 30 m → β_base = 0.20
  //   acc = 60 m → β_base = 0.10
  //   acc =  200 m → β_base = 0.05 (near-floor — likely cell tower noise, ignore drift)
  let beta = Math.max(0.05, Math.min(0.4, 6 / acc));

  // Movement detection: if the new reading is far enough outside the accuracy
  // circle to almost certainly be real motion, ramp β up so we follow quickly.
  // Below 2× accuracy → indistinguishable from noise → keep damping.
  // Above 6× accuracy → confident real motion → β = 0.85 for a fast catch-up.
  // Linear ramp between those points for a smooth transition.
  const ratio = delta / acc;
  if (ratio > 2) {
    const t = Math.min((ratio - 2) / 4, 1);      // 0 at ratio=2, 1 at ratio=6
    beta = beta + (0.85 - beta) * t;
  }

  return Math.max(0.05, Math.min(0.9, beta));
}

/**
 * Factory returning a stateful smoothing filter. Call `.smooth(pos)` for
 * each new GeolocationPosition (or plain {lat,lng,accuracy}) and get a
 * damped {lat, lng, accuracy, beta} back.
 */
export function createCoordFilter() {
  let prev = null; // { lat, lng, accuracy, ts }

  return {
    reset() { prev = null; },

    smooth(input) {
      // Accept either browser GeolocationPosition or a plain object.
      const c = input?.coords ? input.coords : input;
      const lat = Number(c?.latitude ?? c?.lat);
      const lng = Number(c?.longitude ?? c?.lng);
      const acc = Number(c?.accuracy ?? 50);
      const ts = Number(input?.timestamp ?? Date.now());

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return prev ? { ...prev, beta: 0 } : null;
      }

      // First fix — no history, adopt directly.
      if (!prev) {
        prev = { lat, lng, accuracy: acc, ts };
        return { lat, lng, accuracy: acc, beta: 1 };
      }

      // If the previous fix is stale (> 60 s old), treat as first fix again.
      if (ts - prev.ts > 60000) {
        prev = { lat, lng, accuracy: acc, ts };
        return { lat, lng, accuracy: acc, beta: 1 };
      }

      const delta = distanceMeters(prev.lat, prev.lng, lat, lng);
      const beta = computeBeta(acc, delta);

      const smoothedLat = prev.lat + beta * (lat - prev.lat);
      const smoothedLng = prev.lng + beta * (lng - prev.lng);
      // Blend accuracy too — an EMA over accuracy so downstream code sees a stable value.
      const smoothedAcc = prev.accuracy + beta * (acc - prev.accuracy);

      prev = { lat: smoothedLat, lng: smoothedLng, accuracy: smoothedAcc, ts };
      return { lat: smoothedLat, lng: smoothedLng, accuracy: smoothedAcc, beta };
    },

    /** Read-only peek at the current smoothed state (or null). */
    current() { return prev ? { ...prev } : null; },
  };
}

// Named export of the raw math utility for tests / other consumers.
export { distanceMeters };
